import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useSettingsStore } from '../../hooks';
import { useDatabase } from '../../db';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, useToast } from '@lancar-pos/ui';
import { BUSINESS_TYPES, type BusinessType } from '@lancar-pos/shared';
import { seedSampleData } from '../../utils/seedData';

export function OnboardingWizard() {
  const [step, setStep] = useState(1);
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [taxRate, setTaxRate] = useState('11');
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [dataOption, setDataOption] = useState<'fresh' | 'sample'>('fresh');
  const [loading, setLoading] = useState(false);

  const { setSetting, getSetting } = useSettingsStore();
  const db = useDatabase();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const savedBusinessType = getSetting('business_type') as BusinessType | null;
    const savedBusinessName = getSetting('business_name') as string;
    const savedBusinessAddress = getSetting('business_address') as string;
    const savedBusinessPhone = getSetting('business_phone') as string;
    const savedTaxRate = getSetting('tax_rate') as number;
    const savedTaxEnabled = getSetting('tax_enabled') as boolean;

    if (savedBusinessType) setBusinessType(savedBusinessType);
    if (savedBusinessName) setBusinessName(savedBusinessName);
    if (savedBusinessAddress) setBusinessAddress(savedBusinessAddress);
    if (savedBusinessPhone) setBusinessPhone(savedBusinessPhone);
    if (savedTaxRate !== undefined && savedTaxRate !== null) setTaxRate(savedTaxRate.toString());
    if (savedTaxEnabled !== undefined && savedTaxEnabled !== null) setTaxEnabled(savedTaxEnabled);
  }, []);

  const handleNext = () => {
    if (step === 1 && !businessType) {
      toast.error('Pilih jenis usaha');
      return;
    }
    if (step === 2 && !businessName.trim()) {
      toast.error('Nama usaha wajib diisi');
      return;
    }
    setStep(step + 1);
  };

  const handleSkipSheets = async () => {
    await completeOnboarding(false);
  };

  const handleSetupSheets = async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        setSetting('business_type', businessType),
        setSetting('business_name', businessName),
        setSetting('business_address', businessAddress),
        setSetting('business_phone', businessPhone),
        setSetting('tax_rate', parseFloat(taxRate) || 0),
        setSetting('tax_enabled', taxEnabled),
      ]);

      const allSuccess = results.every(r => r.success);

      if (allSuccess) {
        navigate({ to: '/app/sheets-setup', replace: true });
      } else {
        toast.error('Gagal menyimpan beberapa pengaturan');
      }
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const completeOnboarding = async (setupSheets: boolean) => {
    setLoading(true);
    try {
      // Seed sample data if user chose "Data Contoh"
      if (dataOption === 'sample' && db) {
        await seedSampleData(db);
      }

      const results = await Promise.all([
        setSetting('business_type', businessType),
        setSetting('business_name', businessName),
        setSetting('business_address', businessAddress),
        setSetting('business_phone', businessPhone),
        setSetting('tax_rate', parseFloat(taxRate) || 0),
        setSetting('tax_enabled', taxEnabled),
        setSetting('onboarding_completed', true),
        setSetting('sheets_configured', setupSheets),
      ]);

      const allSuccess = results.every(r => r.success);

      if (allSuccess) {
        toast.success('Setup berhasil!', 'Selamat datang di LancarPOS');
        navigate({ to: '/app', replace: true });
      } else {
        toast.error('Gagal menyimpan beberapa pengaturan');
      }
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">LP</span>
            </div>
          </div>
          <CardTitle className="text-center">Selamat Datang di LancarPOS</CardTitle>
          <CardDescription className="text-center">
            Langkah {step} dari 4 - {getStepTitle(step)}
          </CardDescription>
          <div className="flex justify-center gap-2 mt-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 w-8 rounded-full transition-colors ${
                  s === step ? 'bg-primary-600' : s < step ? 'bg-primary-200' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">Pilih jenis usaha Anda:</p>
              {BUSINESS_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setBusinessType(type.value)}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                    businessType === type.value
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{type.label}</div>
                  <div className="text-sm text-gray-500">{type.description}</div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <Input
                label="Nama Usaha"
                placeholder="Contoh: Toko Kopi Jaya"
                value={businessName}
                onChange={setBusinessName}
                required
              />
              <Input
                label="Alamat"
                placeholder="Contoh: Jl. Sudirman No. 123, Jakarta"
                value={businessAddress}
                onChange={setBusinessAddress}
              />
              <Input
                label="Nomor Telepon"
                placeholder="Contoh: 08123456789"
                value={businessPhone}
                onChange={setBusinessPhone}
              />
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="taxEnabled"
                  checked={taxEnabled}
                  onChange={(e) => setTaxEnabled(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <label htmlFor="taxEnabled" className="text-sm text-gray-700">
                  Aktifkan pajak (PPN)
                </label>
              </div>
              {taxEnabled && (
                <Input
                  label="Tarif Pajak (%)"
                  type="number"
                  placeholder="11"
                  value={taxRate}
                  onChange={setTaxRate}
                />
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">Pilih cara memulai data:</p>
              <button
                onClick={() => setDataOption('fresh')}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                  dataOption === 'fresh'
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium text-gray-900">Data Baru</div>
                <div className="text-sm text-gray-500">Mulai dengan database kosong</div>
              </button>
              <button
                onClick={() => setDataOption('sample')}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                  dataOption === 'sample'
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium text-gray-900">Data Contoh</div>
                <div className="text-sm text-gray-500">Mulai dengan produk, kategori, dan satuan contoh</div>
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Sinkronisasi Google Sheets (Opsional)</h4>
                <p className="text-sm text-blue-700">
                  Hubungkan ke Google Sheets untuk mencadangkan data secara otomatis. 
                  Anda bisa mengaturnya nanti di halaman Pengaturan.
                </p>
              </div>
              <p className="text-sm text-gray-600">
                Tanpa Google Sheets, semua data akan disimpan di perangkat ini saja.
              </p>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex gap-3">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={loading}>
              Kembali
            </Button>
          )}
          <div className="flex-1" />
          {step < 4 ? (
            <Button onClick={handleNext}>Lanjut</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleSkipSheets} disabled={loading}>
                Lewati
              </Button>
              <Button onClick={handleSetupSheets} loading={loading}>
                Setup Google Sheets
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}

function getStepTitle(step: number): string {
  switch (step) {
    case 1:
      return 'Pilih Jenis Usaha';
    case 2:
      return 'Informasi Usaha';
    case 3:
      return 'Pilih Data Awal';
    case 4:
      return 'Sinkronisasi Data';
    default:
      return '';
  }
}
